import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import {
  FormTemplate,
  FormTemplateSection
} from 'data/template/template.types';
import * as types from 'data/snmp/snmp.types';
import Form, { FieldUpdateHandlerParam } from 'shared/form';
import './snmp-user-form.scss';
import { FormLayout } from 'components/FormLayout';
import SectionLabel from 'components/SectionLabel';

type Props = {
  template: FormTemplate;
  data: Partial<types.SnmpV3User>;
  i18nKeyPrefix?: string;
  isEditing?: boolean;
  onFieldUpdate?: (
    param: FieldUpdateHandlerParam,
    formSection?: 'main'
  ) => void;
};

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean;
  getValues: () => types.SnmpV3User;
};

export const SNMP_USER_I18N_KEY = 'snmpUser.form';

export const SnmpUserForm = forwardRef<Ref, Props>(
  (
    {
      template,
      data,
      i18nKeyPrefix = SNMP_USER_I18N_KEY,
      onFieldUpdate = () => null,
    }: Props,
    ref
  ) => {
    const mainRef = useRef<Form>(null);

    const customFieldComponentMap = {
      privateKey: SectionLabel,
      certificate: SectionLabel,
      xccaCertParam: SectionLabel
    };

    const isValid = (forceValidation = false, forceTouched = true) => {
      const isMainValid =
        mainRef.current?.isValid(forceValidation, forceTouched) ?? true;
      return isMainValid;
    };

    const getValues = () => {
      const formValues: types.SnmpV3User = {
        ...mainRef.current?.getValues()
      };
      return formValues;
    };

    useImperativeHandle(ref, () => ({
      isValid,
      getValues
    }));

    const onMainFieldUpdate = (param: FieldUpdateHandlerParam) =>
      onFieldUpdate(param, 'main');

    return (
      <div
        data-testid="SnmpUserForm"
      >
        <div data-testid="SnmpUserFormMain">
          <FormLayout>
            <Form
              ref={mainRef}
              name={template.formId ?? template.key}
              defaults={data}
              fields={(template.main as FormTemplateSection).fields}
              layout={
                (template.main as FormTemplateSection).metadata.layout?.rows
              }
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={onMainFieldUpdate}
              customFieldComponentMap={customFieldComponentMap}
            />
          </FormLayout>
        </div>
      </div>
    );
  }
);

export default SnmpUserForm;
